/**
 * Plays a project: slide sequencing, instructions, navigation, end-of-slide
 * reinforcement and results (PlayerController.cs / ButtonsManagerPlayer.cs /
 * QuizzesPlayerController.cs).
 *
 * Flow when a slide ends (PlayerController.OnSlideFinished):
 *  - games with positive reinforcement per slide → EndSlideOverlay, tap → next
 *  - other games → next slide after 2.5 s
 *  - quizzes → the "go on" button at the bottom (ranking later), tap → next
 * After the last slide, games with reinforcement show EndProjectOverlay (tap →
 * results); everything else goes to the results page directly.
 *
 * Played from the editor (`testFrom`, PlayerManager.origin == Origin.editor) it
 * is a test run: it starts on the slide being edited, keeps the slides in order,
 * quizzes skip the name and the ranking and record nothing, back / next are
 * always there (but at the ends) and close too, and the end goes back to the
 * editor.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Project, Slide } from "@makeit/schema";
import { PlayerContext, usePlayer, type SlideResult } from "./context";
import { Stage, STAGE_W, STAGE_H } from "./Stage";
import { SlideView } from "./SlideView";
import { TextBox, useElementSound } from "./elements";
import { libraryUrl, templateUrl } from "../data/media";
import { shuffled } from "./util";
import { setLanguage, t } from "./i18n";
import { fontFamilyFor } from "./fonts";
import { isQuizProject } from "./activity";
import { playFx, preloadFx } from "./sounds";
import { EndProjectOverlay, EndSlideOverlay } from "./Reinforcement";
import { Clock, clockSeconds } from "./Clock";
import { useBackgroundMusic } from "./music";
import { NamePanel, NextButton, RankingPanel } from "./QuizPanels";
import { addAnswer, clearLocalAnswers, fetchRanking, loadLocalAnswers, markFinished, startAnswers, type MyAnswers, type RankingEntry } from "../data/answers";
import { gamePuntuation, sendGameResult } from "../data/gameResults";

type QuizPhase = "name" | "play" | "ranking";

export interface PlayerStudent {
  classroomId: string;
  studentId: string;
  name: string;
}

export function Player({
  project,
  onExit: exitPlayer,
  localAnswers = false,
  student = null,
  testFrom,
}: {
  project: Project;
  onExit?: () => void;
  /** demos: keep quiz answers on this device only */
  localAnswers?: boolean;
  /** a classroom student: quizzes skip the name panel and tag the answers */
  student?: PlayerStudent | null;
  /** played from the editor: the id of the slide to start on (a test run) */
  testFrom?: number;
}) {
  const testing = testFrom != null;
  const slides = useMemo(
    () => (project.settings.randomSlides && !testing ? shuffled(project.slides) : project.slides),
    [project, testing],
  );
  const startIndex = testing ? Math.max(0, slides.findIndex((s) => s.id === testFrom)) : 0;
  const [index, setIndexState] = useState(startIndex);
  const [results, setResults] = useState<SlideResult[]>([]);
  const [finished, setFinished] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [slideResult, setSlideResult] = useState<SlideResult | null>(null);
  const [slideStartedAt, setSlideStartedAt] = useState(() => Date.now());
  const [timeUp, setTimeUp] = useState(false);
  const isQuiz = isQuizProject(project.type);
  useBackgroundMusic(project, !finished);
  const cloudAnswers = isQuiz && !localAnswers;
  const [quizPhase, setQuizPhase] = useState<QuizPhase>(isQuiz && !testing ? "name" : "play");
  const [mine, setMine] = useState<MyAnswers | null>(null);
  const [ranking, setRanking] = useState<RankingEntry[] | null>(null);
  useEffect(() => {
    if (project.language && project.language !== "unknown") setLanguage(project.language);
    preloadFx();
  }, [project.language]);
  const slide: Slide | undefined = slides[index];
  if (import.meta.env.DEV) (window as unknown as { __mkSlide?: number }).__mkSlide = slide?.id; // e2e tests
  // Changing slide restarts its clock in the same render (the Clock reads
  // slideStartedAt on mount, so it must never see the previous slide's value).
  const setIndex = useCallback((i: number) => {
    setIndexState(i);
    setSlideStartedAt(Date.now());
    setTimeUp(false);
  }, []);

  // QuizzesPlayerController.Start: a quiz already played on this device goes straight to the
  // ranking (resuming an unfinished one), unless the project allows repeating.
  useEffect(() => {
    if (!isQuiz || testing) return;
    const stored = loadLocalAnswers(project.id, student?.studentId);
    if (!stored || project.settings.allowRepeat) return;
    setMine(stored);
    if (!stored.finished && stored.answers.length > 0) {
      const last = stored.answers[stored.answers.length - 1].slideId;
      const i = slides.findIndex((sl) => sl.id === last);
      if (i >= 0) setIndex(i);
    } else if (stored.finished) {
      setFinished(true);
    }
    setQuizPhase("ranking");
    setRanking(null);
    void fetchRanking(stored, slides.map((sl) => sl.id), cloudAnswers).then(setRanking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const pr = project.settings.positiveReinforcement;
  const prEachSlide = pr !== "none" && project.settings.prEachSlide && !isQuiz;
  const prEndProject = pr !== "none" && !isQuiz;
  const showScore = project.settings.prStarsShowScore;
  const hasClock = isQuiz && project.settings.timer === "timer";

  // GamesAnswerCollector: a classroom student playing a game sends how it went —
  // when the game ends, or when they close it (with the page reached). The time
  // counts the seconds the game was on screen (CountTime).
  const collectGame = !!student && !isQuiz && !localAnswers;
  const playedSeconds = useRef(0);
  const resultSent = useRef(false);
  const resultsRef = useRef(results);
  resultsRef.current = results;
  useEffect(() => {
    if (!collectGame) return;
    const id = window.setInterval(() => {
      if (!document.hidden) playedSeconds.current += 1;
    }, 1000);
    return () => window.clearInterval(id);
  }, [collectGame]);
  const sendResult = useCallback(
    (done: boolean, pages: number) => {
      if (!collectGame || !student || resultSent.current) return;
      resultSent.current = true;
      void sendGameResult({
        projectId: project.id,
        classroomId: student.classroomId,
        studentId: student.studentId,
        puntuation: gamePuntuation(slides, resultsRef.current),
        finished: done,
        pages,
        time: playedSeconds.current,
      }).catch(() => undefined);
    },
    [collectGame, student, project.id, slides],
  );
  const onExit = useMemo(
    () =>
      exitPlayer
        ? () => {
            sendResult(false, index + 1); // ButtonsManagerPlayer.CloseButton
            exitPlayer();
          }
        : undefined,
    [exitPlayer, sendResult, index],
  );

  const next = useCallback(() => {
    setSlideResult(null);
    if (index + 1 < slides.length) {
      setIndex(index + 1);
    } else {
      setFinished(true);
      sendResult(true, slides.length); // PlayerController.NextSlide on the last page
      if (prEndProject) playFx("congratulations", project.settings, 1000);
      else setShowResults(true);
    }
  }, [index, slides.length, prEndProject, project.settings, sendResult]);

  const prev = useCallback(() => {
    if (index > 0) {
      setSlideResult(null);
      setIndex(index - 1);
    }
  }, [index]);

  // Free-element links are slide indexes (LinkElementPlayer → PlayerController.JumpToSlide).
  const goToSlide = useCallback(
    (slideIndex: number) => {
      if (slideIndex >= 0 && slideIndex < slides.length) {
        setSlideResult(null);
        setIndex(slideIndex);
      }
    },
    [slides.length, setIndex],
  );

  const isLast = index + 1 >= slides.length;
  const recordAnswer = useCallback(
    (full: SlideResult) => {
      if (!isQuiz || !mine) return;
      void addAnswer(mine, { slideId: full.slideId, answer: full.answer ?? "", correct: full.correct, points: full.points }, isLast).then(setMine);
    },
    [isQuiz, mine, isLast],
  );

  const finish = useCallback(
    (r: Omit<SlideResult, "slideId">) => {
      if (!slide) return;
      const full = { ...r, slideId: slide.id };
      if (import.meta.env.DEV) ((window as unknown as { __mkResults?: SlideResult[] }).__mkResults ??= []).push(full); // e2e tests
      setResults((rs) => [...rs.filter((x) => x.slideId !== slide.id), full]);
      setSlideResult(full);
      recordAnswer(full);
      if (prEachSlide && r.correct) playFx("congratulations", project.settings, 1000);
    },
    [slide, prEachSlide, project.settings, recordAnswer],
  );

  // Timer.cs → QuizzesPlayerController.TimeEnded → PlayspaceBase.TimeFinished: the slide counts as wrong.
  const onTimeUp = useCallback(() => {
    if (!slide) return;
    setTimeUp(true);
    const full: SlideResult = { slideId: slide.id, correct: false, points: 0, stars: 1, answer: "" };
    setResults((rs) => [...rs.filter((x) => x.slideId !== slide.id), full]);
    setSlideResult(full);
    recordAnswer(full);
  }, [slide, recordAnswer]);

  // Quiz flow: name → questions, each followed by the ranking (QuizzesPlayerController).
  const startQuiz = useCallback(
    (name: string) => {
      void startAnswers(project, name, cloudAnswers, student ? { classroomId: student.classroomId, studentId: student.studentId } : undefined).then((m) => {
        setMine(m);
        setQuizPhase("play");
        setSlideStartedAt(Date.now());
      });
    },
    [project, cloudAnswers, student],
  );
  // Classroom students never type a name: the quiz starts with theirs.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!isQuiz || !student || quizPhase !== "name" || mine || autoStarted.current) return;
    autoStarted.current = true;
    startQuiz(student.name);
  }, [isQuiz, student, quizPhase, mine, startQuiz]);
  const openRanking = useCallback(() => {
    setQuizPhase("ranking");
    setRanking(null);
    const played = slides.slice(0, index + 1).map((sl) => sl.id);
    if (mine) void fetchRanking(mine, played, cloudAnswers).then(setRanking);
  }, [slides, index, mine, cloudAnswers]);
  const closeRanking = useCallback(() => {
    if (finished || isLast) {
      if (mine) void markFinished(mine).then(setMine);
      setFinished(true);
      setShowResults(true);
      return;
    }
    setQuizPhase("play");
    next();
  }, [finished, isLast, mine, next]);

  // Games without per-slide reinforcement move on by themselves (Invoke(NextSlide, 2.5f)).
  useEffect(() => {
    if (!slideResult || isQuiz || prEachSlide) return;
    const id = window.setTimeout(next, 2500);
    return () => window.clearTimeout(id);
  }, [slideResult, isQuiz, prEachSlide, next]);

  // Keyboard navigation (handy on desktop / for QA).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isQuiz && quizPhase !== "play") return;
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, isQuiz, quizPhase]);

  // A test run ends back in the editor (PlayerController.CloseProject, Origin.editor).
  useEffect(() => {
    if (testing && finished && showResults) onExit?.();
  }, [testing, finished, showResults, onExit]);

  const replay = () => {
    resultSent.current = false;
    playedSeconds.current = 0;
    setIndex(0);
    setResults([]);
    setFinished(false);
    setShowResults(false);
    setSlideResult(null);
    if (isQuiz) {
      clearLocalAnswers(project.id);
      setMine(null);
      setQuizPhase("name");
    }
  };

  if (!slide) {
    return <Empty onExit={onExit} />;
  }
  if (finished && showResults) {
    return <Results project={project} results={results} slides={slides} points={mine?.totalPoints} onExit={onExit} onReplay={isQuiz && !project.settings.allowRepeat ? undefined : replay} />;
  }

  const ctx = { project, slide, slideIndex: index, slideCount: slides.length, slideStartedAt, timeUp, finish, goToSlide, next, prev };
  const bg = slide.background ?? null;
  const totalStars = results.reduce((a, r) => a + r.stars, 0);

  return (
    <PlayerContext.Provider value={ctx}>
      <Stage
        background={
          <div
            className="stage-bg"
            style={{
              position: "absolute",
              inset: 0,
              background: project.template.startsWith("#") ? project.template : undefined,
              backgroundImage: bg ? undefined : `url(${templateUrl(project.template, "background")})`,
              backgroundSize: "cover",
            }}
          />
        }
      >
        <div className="slide" style={{ position: "absolute", width: STAGE_W, height: STAGE_H, fontFamily: fontFamilyFor(project.settings.font) }}>
          <SlideView key={slide.id} slide={slide} />
        </div>
        {quizPhase === "play" && <Instructions key={`i${slide.id}`} slide={slide} />}
        {!finished && quizPhase === "play" && (
          <Hud
            index={index}
            slide={slide}
            autoNavigation={project.settings.autoNavigation}
            testing={testing ? { last: isLast } : undefined}
            onPrev={prev}
            onNext={next}
            onExit={isQuiz && !testing ? undefined : onExit}
          />
        )}
        {hasClock && quizPhase === "play" && slide.type !== "free" && !slideResult && !finished && (
          <Clock key={`c${slide.id}`} seconds={clockSeconds(slide.seconds)} startedAt={slideStartedAt} onTimeUp={onTimeUp} />
        )}
        {isQuiz && quizPhase === "play" && slideResult && !finished && <NextButton onClick={testing ? next : openRanking} />}
        {isQuiz && quizPhase === "name" && <NamePanel onStart={startQuiz} onCancel={onExit} />}
        {isQuiz && quizPhase === "ranking" && (
          <RankingPanel entries={ranking} hideScore={project.settings.hideScore} showRanking={project.settings.showRanking} onNext={closeRanking} onExit={onExit} />
        )}
        {prEachSlide && slideResult && !finished && (
          <EndSlideOverlay key={`e${slide.id}`} correct={slideResult.correct} stars={slideResult.stars} showScore={showScore} onContinue={next} />
        )}
        {finished && prEndProject && (
          <EndProjectOverlay totalStars={totalStars} showScore={showScore} font={project.settings.font} onClose={() => setShowResults(true)} />
        )}
      </Stage>
    </PlayerContext.Provider>
  );
}

function Instructions({ slide }: { slide: Slide }) {
  const { project } = usePlayer();
  const el = slide.instructions;
  const { play, hasSound } = useElementSound(el);
  const [open, setOpen] = useState(() => !!el && (!!el.text.content || !!el.sound));
  useEffect(() => {
    if (open && hasSound) play();
  }, [open, hasSound, play]);
  if (!el || !open) return null;
  return (
    <div className="instructions" onClick={() => setOpen(false)}>
      <div className="instructions-panel" style={{ fontFamily: fontFamilyFor(project.settings.font) }}>
        <TextBox text={el.text} fit />
        {hasSound && <span className="sound-badge" aria-hidden>♪</span>}
      </div>
    </div>
  );
}

/**
 * Navigation buttons (PlayerScene › CanvasNavigation, PlayerController.ResetNavigationButtons):
 * close + back at the top-left, next at the top-right. With auto navigation the
 * next button only shows on free/paint slides and back never shows. A test run
 * from the editor always has both, except back on the first slide and next on
 * the last one.
 */
function Hud({ index, slide, autoNavigation, testing, onPrev, onNext, onExit }: { index: number; slide: Slide; autoNavigation: boolean; testing?: { last: boolean }; onPrev: () => void; onNext: () => void; onExit?: () => void }) {
  const showNext = testing ? !testing.last : autoNavigation ? slide.type === "free" || slide.type === "paint" : true;
  const showPrev = index > 0 && (!!testing || !autoNavigation);
  return (
    <>
      <div className="nav nav-left">
        {onExit && <button className="nav-btn" onClick={onExit} aria-label={t("exit")} style={{ backgroundImage: `url(${libraryUrl("ui/navigationClose.png")})` }} />}
        {showPrev && <button className="nav-btn" onClick={onPrev} aria-label={t("back")} style={{ backgroundImage: `url(${libraryUrl("ui/navigationBack.png")})` }} />}
      </div>
      {showNext && (
        <div className="nav nav-right">
          <button className="nav-btn" onClick={onNext} aria-label={t("next")} style={{ backgroundImage: `url(${libraryUrl("ui/navigationNext.png")})` }} />
        </div>
      )}
    </>
  );
}

function Results({ project, results, slides, points: quizPoints, onExit, onReplay }: { project: Project; results: SlideResult[]; slides: Slide[]; points?: number; onExit?: () => void; onReplay?: () => void }) {
  const scored = slides.filter((s) => s.type !== "free");
  const correct = results.filter((r) => r.correct).length;
  const points = quizPoints ?? results.reduce((a, r) => a + r.points, 0);
  const stars = results.reduce((a, r) => a + r.stars, 0);
  const isQuiz = isQuizProject(project.type);
  return (
    <div className="results">
      <h1>{project.name}</h1>
      {scored.length > 0 && !project.settings.hideScore && (
        <p className="results-score">
          {isQuiz ? `${correct} / ${scored.length} · ${points} ${t("points")}` : `${correct} / ${scored.length} · ${stars} ★`}
        </p>
      )}
      <div className="results-actions">
        {onReplay && <button className="btn" onClick={onReplay}>{t("playAgain")}</button>}
        {onExit && <button className="btn btn-secondary" onClick={onExit}>{t("exit")}</button>}
      </div>
    </div>
  );
}

function Empty({ onExit }: { onExit?: () => void }) {
  return (
    <div className="results">
      <p>{t("noSlides")}</p>
      {onExit && <button className="btn" onClick={onExit}>{t("exit")}</button>}
    </div>
  );
}
